// BasicLoginGate — blocks the UI behind a username/password form in
// self-host basic-auth mode when no session is active. Silent in
// Supabase mode and when already signed in.

import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';

export function BasicLoginGate({ children }: { children: React.ReactNode }) {
  const { mode, user, isLoading, defaultPasswordWarning, signInWithPassword } = useAuth();
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Non-basic mode or already signed in — render app normally.
  if (mode !== 'basic') return <>{children}</>;
  if (isLoading) return null;
  if (user) return <>{children}</>;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithPassword(username.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  }

  // Wrap with `dark` to force dark styling on the login gate regardless
  // of the user's system preference — the self-host login is always a
  // developer-facing screen and looks better dark than light.
  return (
    <div className="dark flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm border border-rule bg-panel p-6">
        <div className="mb-5">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center bg-accent text-sm font-bold text-white">
              ≡
            </span>
            <h1 className="text-base font-semibold text-ink">
              llmcontext <span className="text-dim">· self-hosted</span>
            </h1>
          </div>
          <p className="mt-2 text-xs text-dim">
            Sign in with the admin credentials configured in this instance's{' '}
            <code className="font-mono">.env</code>.
          </p>
        </div>

        {defaultPasswordWarning && (
          <div className="mb-4 border border-warn/30 bg-warn/15 px-3 py-2 text-xs text-warn">
            <strong className="font-semibold">Default password in use.</strong> Set{' '}
            <code className="font-mono">BASIC_AUTH_PASSWORD</code> in{' '}
            <code className="font-mono">.env</code> immediately.
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
          <label className="block text-xs font-medium text-link">
            Username
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="lc-input mt-1 py-1.5"
            />
          </label>
          <label className="block text-xs font-medium text-link">
            Password
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="lc-input mt-1 py-1.5"
            />
          </label>

          {error && (
            <div className="border border-del/30 bg-del/15 px-3 py-2 text-xs text-del">{error}</div>
          )}

          <Button
            type="submit"
            size="sm"
            disabled={submitting || !username.trim() || !password}
            className="w-full"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-4 text-center text-2xs text-dim">
          Defaults: <code className="font-mono">admin</code> /{' '}
          <code className="font-mono">admin</code> — change them via{' '}
          <code className="font-mono">BASIC_AUTH_USERNAME</code> and{' '}
          <code className="font-mono">BASIC_AUTH_PASSWORD</code>.
        </p>
      </div>
    </div>
  );
}
